<!-- src/components/UploadDragAndDRop.vue -->
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CropDialog from '@/components/CropDialog/CropDialog.vue'
import addFile from '@/assets/icons/green/add_photo_alternate.svg'
import { useToast } from 'vue-toastification'
const toast = useToast()

type CropShape = 'square' | 'circle'

const props = withDefaults(
  defineProps<{
    preview: string | null
    cropShape?: 'square' | 'circle'
    accept: string
    maxSizeMb?: number
    disabled?: boolean
    forceMime?: 'image/jpeg' | 'image/png'
    jpegBg?: string
    alternativeText?: string
  }>(),
  {
    preview: null,
    cropShape: 'square',
    accept: 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml',
    maxSizeMb: 5,
    disabled: false,
    forceMime: 'image/jpeg',
    jpegBg: '#FFFFFF',
    alternativeText: '',
  }
)

const emit = defineEmits<{
  (e: 'save', dataUrl: string): void
  (e: 'remove'): void
  (e: 'replace'): void
  (e: 'delete'): void
  (e: 'update:preview', value: string | null): void
}>()

const locallyCleared = ref(false)
watch(
  () => props.preview,
  () => {
    locallyCleared.value = false
  }
)

const { t } = useI18n({ useScope: 'global' })

const fileInput = ref<HTMLInputElement | null>(null)
const cropOpen = ref(false)
const imageForCrop = ref<string | undefined>(undefined)

const hasPreview = computed<boolean>(
  () => Boolean(props.preview) && !locallyCleared.value
)

const ACCEPT_DEFAULT = computed<string>(
  () =>
    props.accept ?? 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml'
)
const MAX_SIZE = computed<number>(() => props.maxSizeMb * 1024 * 1024)
const cropType = computed<CropShape>(() => props.cropShape ?? 'square')

function toDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = (e) => reject(e)
    reader.readAsDataURL(file)
  })
}

function openPicker(): void {
  if (props.disabled) return
  fileInput.value?.click()
}

async function onFileChange(e: Event): Promise<void> {
  if (props.disabled) return
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  if (file.size > MAX_SIZE.value) {
    toast.error(
      `${t('validation.fileTooLarge', 'Arquivo muito grande')}: ${props.maxSizeMb}MB`,
      {
        timeout: 4000,
        icon: 'mdi-alert-circle-outline',
      }
    )
    input.value = ''
    return
  }

  imageForCrop.value = await toDataUrl(file)
  cropOpen.value = true
  emit('replace')
  input.value = ''
}

async function onDrop(e: DragEvent): Promise<void> {
  if (props.disabled) return
  e.preventDefault()
  const file = e.dataTransfer?.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) return

  if (file.size > MAX_SIZE.value) {
    alert(
      `${t('validation.fileTooLarge', 'Arquivo muito grande')}: ${props.maxSizeMb}MB`
    )
    return
  }

  imageForCrop.value = await toDataUrl(file)
  cropOpen.value = true
}

function onDragOver(e: DragEvent): void {
  if (props.disabled) return
  e.preventDefault()
}

function handleRemove(): void {
  if (props.disabled) return
  locallyCleared.value = true
  emit('update:preview', null)
  emit('remove')
  emit('delete') // back-compat
}

/** Preview: se vier relativo (sem http/https/blob/data), prefixa /resources */
const resolvedPreview = computed<string | undefined>(() => {
  const p = props.preview ?? undefined
  if (!p) return undefined
  const isAbsolute = /^https?:|^blob:|^data:/.test(p)
  return isAbsolute ? p : `${import.meta.env.VITE_API_URL}/resources/${p}`
})

function dataUrlToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const c = document.createElement('canvas')
      c.width = img.width
      c.height = img.height
      const ctx = c.getContext('2d')!
      ctx.fillStyle = props.jpegBg
      ctx.fillRect(0, 0, c.width, c.height) // fundo branco
      ctx.drawImage(img, 0, 0)
      resolve(c)
    }
    img.src = dataUrl
  })
}

async function normalizeToMime(dataUrl: string): Promise<string> {
  if (props.forceMime === 'image/jpeg') {
    const canvas = await dataUrlToCanvas(dataUrl)
    return canvas.toDataURL('image/jpeg', 0.92)
  }
  return dataUrl
}

async function handleCropped(dataUrl: string | null): Promise<void> {
  cropOpen.value = false
  if (!dataUrl) return
  const normalized = await normalizeToMime(dataUrl)
  emit('save', normalized)
}

const displayText = computed(() =>
  props.alternativeText?.trim()
    ? props.alternativeText
    : t('general.components.uploadDragAndDrop.mainText')
)
</script>

<template>
  <div class="udnd">
    <!-- COM IMAGEM -->
    <div v-if="hasPreview" class="udnd__row">
      <!-- Thumb -->
      <div
        class="udnd__thumb"
        :style="{ borderRadius: cropType === 'circle' ? '9999px' : '12px' }"
      >
        <img
          id="file-input-preview"
          class="udnd__thumb-img"
          :src="resolvedPreview"
          alt="Event image"
        />
      </div>

      <!-- Conteúdo -->
      <div class="udnd__content">
        <div class="udnd__title-line">
          <span class="udnd__title">
            {{ displayText }}
          </span>
        </div>

        <p class="udnd__fineprint">
          {{ t('general.components.uploadDragAndDrop.maximumSize') }}<br />
          {{ t('general.components.uploadDragAndDrop.recomendedWitdhHeight')
          }}<br />
          {{ t('general.components.uploadDragAndDrop.observation') }}
        </p>

        <div class="udnd__cta">
          <button
            class="udnd__link udnd__link--success"
            type="button"
            @click="openPicker"
          >
            {{ t('general.components.uploadDragAndDrop.replace') }}
          </button>
          <button
            class="udnd__link udnd__link--danger"
            type="button"
            @click="handleRemove"
          >
            {{ t('general.components.uploadDragAndDrop.remove') }}
          </button>
        </div>
      </div>
    </div>

    <!-- SEM IMAGEM -->
    <div
      v-else
      class="udnd__empty"
      :class="{ 'is-disabled': disabled }"
      role="button"
      tabindex="0"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @dragover="onDragOver"
      @drop="onDrop"
    >
      <div class="udnd__empty-left">
        <div class="udnd__empty-thumb">
          <div class="udnd__ph">
            <img :src="addFile" alt="Upload" class="udnd__ph-icon" />
            <span class="udnd__ph-text">{{
              t('general.components.uploadDragAndDrop.drag')
            }}</span>
          </div>
        </div>
      </div>

      <div class="udnd__empty-right">
        <span class="udnd__title">
          {{ displayText }}
        </span>
        <p class="udnd__fineprint">
          {{ t('general.components.uploadDragAndDrop.maximumSize') }}<br />
          {{ t('general.components.uploadDragAndDrop.recomendedWitdhHeight')
          }}<br />
          {{ t('general.components.uploadDragAndDrop.observation') }}
        </p>
      </div>
    </div>

    <!-- input invisível -->
    <input
      ref="fileInput"
      :accept="ACCEPT_DEFAULT"
      type="file"
      class="udnd__input"
      :disabled="disabled"
      @change="onFileChange"
    />

    <!-- Cropper -->
    <CropDialog
      :open="cropOpen"
      :image="imageForCrop"
      :type="cropType"
      @update:dialog="(v: boolean) => (cropOpen = v)"
      @close="cropOpen = false"
      @save="handleCropped"
    />
  </div>
</template>

<style scoped>
/* ---------- ROOT ---------- */
.udnd {
  width: 100%;
}

/* ---------- FILLED (preview visível) ---------- */
.udnd__row {
  display: grid;
  grid-template-columns: 200px 1fr; /* thumb fixa, textos flexíveis */
  gap: 24px;
  align-items: center; /* centraliza verticalmente o bloco de texto no desktop */
}

.udnd__thumb {
  width: 200px;
  height: 200px;
  background: #fff;
  border-radius: 12px;
}

.udnd__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.udnd__content {
  display: flex;
  flex-direction: column;
  justify-content: center; /* alinha verticalmente */
  gap: 8px;
  min-height: 200px; /* mesma altura do quadrado */
  text-align: left;
}

.udnd__title-line {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.udnd__title {
  font-weight: 600;
  color: #333;
}

.udnd__fineprint {
  margin-top: 2px;
  font-size: 12px;
  color: #777;
  line-height: 1.4;
  text-align: left;
}

/* ---------- CTA: Trocar / Remover ---------- */
.udnd__cta {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  padding: 8px 0;
}

/* Base dos dois botões */
.udnd__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 8px 16px; /* ⬅️ padding com 16px nas laterais */
  background-color: transparent;
  cursor: pointer;

  font-weight: 600;
  font-size: 14px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.15s ease;
}

/* Suporte amplo (fallback): se não houver color-mix, use filter no hover */
.udnd__link:hover {
  background-color: #f5f5f5; /* ⬅️ fundo cinza no hover */
}

/* “Substituir” (verde) */
.udnd__link--success {
  color: #70a835;
  border-color: #70a83540;
}
.udnd__link--success:hover {
  transform: translateY(-1px);
}
@supports (color: color-mix(in oklab, black, white)) {
  .udnd__link--success:hover {
    color: color-mix(in oklab, #70a835 80%, black 20%); /* ~20% mais escuro */
  }
}

/* “Remover” (vermelho) */
.udnd__link--danger {
  color: #e64a4a;
  border-color: #e64a4a40;
}
.udnd__link--danger:hover {
  transform: translateY(-1px);
}
@supports (color: color-mix(in oklab, black, white)) {
  .udnd__link--danger:hover {
    color: color-mix(in oklab, #e64a4a 80%, black 20%); /* ~20% mais escuro */
  }
}

/* Estados extras */
.udnd__link:active {
  transform: scale(0.98);
  opacity: 0.95;
}
.udnd__link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px #c8d0c6;
}
.udnd__dot {
  color: #999;
  user-select: none;
}

/* ---------- EMPTY (sem preview) ---------- */
.udnd__empty {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  align-items: center;
  cursor: pointer;
}
.udnd__empty.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.udnd__empty-left {
  width: 200px;
  height: 200px;
}

.udnd__empty-thumb {
  width: 200px;
  height: 200px;
  border: 1px dashed #c8d0c6;
  background: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.udnd__ph {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.udnd__ph-icon {
  width: 24px;
  height: 24px;
  display: block;
  pointer-events: none;
}
.udnd__ph-text {
  font-size: 12px;
  line-height: 1.2;
  color: #666;
  max-width: 140px;
}

.udnd__empty-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  min-height: 200px;
  text-align: left;
}

/* ---------- HIDDEN INPUT ---------- */
.udnd__input {
  position: absolute;
  inset: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

/* ---------- RESPONSIVO ---------- */
@media (max-width: 960px) {
  .udnd__row,
  .udnd__empty {
    grid-template-columns: 1fr; /* empilha */
    gap: 16px;
    justify-items: left;
  }

  .udnd__content,
  .udnd__empty-right {
    min-height: initial;
    padding: 0;
    align-items: flex-start;
    text-align: left;
  }

  .udnd__thumb,
  .udnd__empty-left,
  .udnd__empty-thumb {
    width: 200px;
    height: 200px;
  }

  .udnd__cta {
    justify-content: flex-start;
  }
}

/* (opcionais seus antigos, mantidos se usados em outro lugar do app) */
.actionbutton {
  border-radius: 4px !important;
  box-shadow: none !important;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease;
}
.textActionButton {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: filter 0.2s ease;
}
.textActionButton:hover {
  filter: brightness(0.8);
}
</style>
